import { useMemo, useReducer } from 'react';

import { ProgressChecklistStep } from '@context/progress-checklist-context';

import type { ProgressChecklistAction } from '@context/progress-checklist-context';

interface UseProgressChecklistReturnValue {
  show: boolean;
  steps: ProgressChecklistStep[];
  dispatch: (action: ProgressChecklistAction) => void;
}

interface ProgressChecklistState {
  show: boolean;
  steps: ProgressChecklistStep[];
}

function progressChecklistReducer(
  state: ProgressChecklistState,
  action: ProgressChecklistAction
): ProgressChecklistState {
  switch (action.type) {
    case 'ADD_STEPS':
      return { ...state, steps: addStepsToChecklist(state.steps, action.value, action.overwrite) };
    case 'COMPLETE_CURRENT_STEP':
      return { ...state, steps: completeCurrentStep(state.steps, action.advance) };
    case 'COMPLETE_STEP':
      return {
        ...state,
        steps: completeStep(state.steps, action.value, action.advance, action.completePreviousSteps),
      };
    case 'MAKE_STEP_ACTIVE':
      return { ...state, steps: activateStep(state.steps, action.value, action.completePreviousSteps) };
    case 'INITIALIZE_CHECKLIST':
      return { ...state, steps: validateSteps(action.value), show: true };
    case 'FINISH':
      return { ...state, steps: completeCurrentStep(state.steps, false), show: false };
    case 'MOVE_TO_PREV_STEP':
      return { ...state, steps: moveToPreviousStep(state.steps) };
    case 'SHOW':
      return { ...state, show: true };
  }
}

function moveToPreviousStep(checklist: ProgressChecklistStep[]): ProgressChecklistStep[] {
  let currentStepIndex: number | undefined;

  return [...checklist]
    .reverse()
    .map((step, i) => {
      if (step.active || step.completed) {
        currentStepIndex = i;
        return { ...step, active: false, completed: false };
      } else if (currentStepIndex && i === currentStepIndex + i) {
        return { ...step, active: true, completed: false };
      } else return step;
    })
    .reverse();
}

function addStepsToChecklist(
  checklist: ProgressChecklistStep[],
  newSteps: ProgressChecklistStep[],
  overwrite = false
): ProgressChecklistStep[] {
  validateSteps(newSteps);
  if (overwrite) return newSteps;

  const newChecklist = [...checklist, ...newSteps];
  validateSteps(
    newChecklist,
    'After adding steps the checklist is no longer valid. Only one step can be active at a time.'
  );

  return newChecklist;
}

function activateStep(
  checklist: ProgressChecklistStep[],
  id: string,
  completePreviousSteps: boolean | string[] = true
): ProgressChecklistStep[] {
  const stepIndex = checklist.findIndex((step) => step.id === id);
  if (stepIndex === -1) return checklist;

  // Only allow activation of steps that have not yet been activated
  if (!checklist[stepIndex].completed) {
    return checklist.map((step, i) => {
      if (i < stepIndex) {
        return {
          ...step,
          active: false,
          completed: isPreviousStepCompleted(step, completePreviousSteps),
        };
      } else if (i === stepIndex) {
        return { ...step, active: true, completed: false };
      } else {
        return { ...step, active: false, completed: false };
      }
    });
  } else return checklist;
}

function completeStep(
  checklist: ProgressChecklistStep[],
  id: string,
  advanceToNextStep = true,
  completePreviousSteps: boolean | string[] = true
): ProgressChecklistStep[] {
  const stepIndex = checklist.findIndex((step) => step.id === id);
  if (stepIndex === -1) return checklist;

  return checklist.map((step, i) => {
    if (i < stepIndex) {
      return {
        ...step,
        active: false,
        completed: isPreviousStepCompleted(step, completePreviousSteps),
      };
    } else if (i === stepIndex) {
      return { ...step, active: false, completed: true };
    } else if (i === stepIndex + 1 && advanceToNextStep) {
      return { ...step, active: true, completed: false };
    } else {
      return { ...step, active: false, completed: false };
    }
  });
}

function isPreviousStepCompleted(step: ProgressChecklistStep, completePreviousSteps: boolean | string[]) {
  if (typeof completePreviousSteps === 'boolean') {
    return completePreviousSteps ? true : step.completed;
  } else {
    return completePreviousSteps.includes(step.id);
  }
}

function completeCurrentStep(
  checklist: ProgressChecklistStep[],
  advanceToNextStep = true
): ProgressChecklistStep[] {
  let activeStepIndex: number | undefined;

  return checklist.map((step, i) => {
    if (step.active) {
      activeStepIndex = i;
      return { ...step, active: false, completed: true };
    } else if (advanceToNextStep && activeStepIndex !== undefined && i === activeStepIndex + 1) {
      return { ...step, active: true };
    } else return step;
  });
}

function validateSteps(steps: ProgressChecklistStep[], errorMessage?: string): ProgressChecklistStep[] {
  if (!areStepsValid(steps)) {
    throw new Error(errorMessage ?? 'Steps provided to checklist are not valid.');
  }

  return steps;
}

function areStepsValid(steps: ProgressChecklistStep[]): boolean {
  let numActiveSteps = 0;

  for (const step of steps) {
    if (!step.name || !step.id) return false;
    if (step.active) {
      numActiveSteps++;
      if (numActiveSteps > 1) return false;
    }
  }

  return true;
}

function isStepCompleted(steps: ProgressChecklistStep[], id: string): boolean {
  const step = steps.find((step) => step.id === id);

  return !!step?.completed;
}

/**
 * Returns the props needed for the progress checklist context
 */
function useProgressChecklist(): UseProgressChecklistReturnValue {
  const [checklistState, dispatch] = useReducer(progressChecklistReducer, { show: false, steps: [] });

  const progressChecklistProps = useMemo(
    () => ({
      show: checklistState.show,
      steps: checklistState.steps,
      dispatch,
    }),
    [checklistState, dispatch]
  );

  return progressChecklistProps;
}

export default useProgressChecklist;
export {
  moveToPreviousStep,
  addStepsToChecklist,
  completeStep,
  completeCurrentStep,
  activateStep,
  validateSteps,
  areStepsValid,
  isStepCompleted,
};
